import { ReportCustomFieldType } from "@shared/lib/types/reports";
import { Button, Input } from "@shared/ui";
import InputNumber from "@shared/ui/InputNumber";
import type { ReportFieldDefault } from "../lib/useReportCustomFieldsForm";
import styles from "../styles.module.scss";

type ReportCustomFieldsFormProps = {
  fieldName: string;
  fieldType: ReportCustomFieldType;
  fieldDefault: ReportFieldDefault;
  description: string;
  isSubmitting: boolean;
  onFieldNameChange: (value: string) => void;
  onFieldTypeChange: (type: ReportCustomFieldType) => void;
  onFieldDefaultChange: (value: ReportFieldDefault) => void;
  onDescriptionChange: (value: string) => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
};

export function ReportCustomFieldsForm({
  fieldName,
  fieldType,
  fieldDefault,
  description,
  isSubmitting,
  onFieldNameChange,
  onFieldTypeChange,
  onFieldDefaultChange,
  onDescriptionChange,
  onSubmit,
  onCancel,
}: ReportCustomFieldsFormProps) {
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit(e);
      }}
      className={styles.form}
    >
      <div className={styles.formGroup}>
        <label className={styles.label} htmlFor="report-field-name">
          Field Name
        </label>
        <Input
          id="report-field-name"
          value={fieldName}
          onChange={(e) => onFieldNameChange(e.target.value)}
          placeholder="e.g. Campaign notes"
          className={styles.input}
          autoFocus
          required
          size="small"
        />
      </div>

      <div className={styles.formGroup}>
        <span className={styles.label}>Data Type</span>
        <div className={styles.typeGridReport}>
          <button
            type="button"
            onClick={() => onFieldTypeChange(ReportCustomFieldType.STRING)}
            className={`${styles.typeButton} ${
              fieldType === ReportCustomFieldType.STRING
                ? styles.typeButtonActive
                : ""
            }`}
          >
            Text
          </button>
          <button
            type="button"
            onClick={() => onFieldTypeChange(ReportCustomFieldType.NUMBER)}
            className={`${styles.typeButton} ${
              fieldType === ReportCustomFieldType.NUMBER
                ? styles.typeButtonActive
                : ""
            }`}
          >
            Number
          </button>
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label} htmlFor="report-field-description">
          Description (optional)
        </label>
        <Input
          id="report-field-description"
          value={description}
          onChange={(e) => onDescriptionChange(e.target.value)}
          placeholder="Short hint for the team"
          className={styles.input}
          size="small"
        />
      </div>

      <div className={styles.formGroup}>
        <label className={styles.label} htmlFor="report-field-default">
          Default value (optional)
        </label>
        {fieldType === ReportCustomFieldType.NUMBER ? (
          <InputNumber
            value={
              typeof fieldDefault === "number"
                ? fieldDefault
                : fieldDefault === "" || fieldDefault === undefined
                  ? undefined
                  : Number(fieldDefault)
            }
            onChange={(value) =>
              onFieldDefaultChange(
                value === null || value === undefined ? undefined : value
              )
            }
            placeholder="Value for new rows"
            className={styles.inputNumber}
            size="small"
          />
        ) : (
          <Input
            id="report-field-default"
            value={
              fieldDefault === undefined || fieldDefault === null
                ? ""
                : String(fieldDefault)
            }
            onChange={(e) => onFieldDefaultChange(e.target.value || undefined)}
            placeholder="Value for new rows"
            className={styles.input}
            size="small"
          />
        )}
      </div>

      <div className={styles.formActions}>
        <Button onClick={onCancel} className={styles.cancelButton} type="default">
          Cancel
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          loading={isSubmitting}
          className={styles.submitButton}
        >
          Create Field
        </Button>
      </div>
    </form>
  );
}
